import { useMemo, useState } from "react";
import { Box, styled } from "@mui/material";
import { Package, MapPin, Boxes, Check } from "lucide-react";
import { EntityThumb } from "@components/EntityThumb/EntityThumb";
import { FieldCard } from "@components/form/FieldCard";
import { FormFooter } from "@components/form/FormFooter";
import { InfoCallout } from "@components/form/InfoCallout";
import { PickerSheet, PickerOption } from "@components/form/PickerSheet";
import { usePrimaryEntityImages } from "@queries/useEntityImages";
import { tokens } from "@styles/tokens";
import { useTransferForm } from "../../../hooks/useTransferForm";

type TransferFormProps = { handleClose: () => void };

/**
 * Transfer-stock drawer — moves a placement's stock to another location, on the
 * shared forms pattern: read-only Item + From context, a destination picker,
 * and a quantity (defaulting to the full amount). Logic lives in
 * useTransferForm; this renders + wires the destination picker.
 */
export function TransferForm({ handleClose }: TransferFormProps) {
  const {
    isLoading,
    product,
    sourceLocation,
    destination,
    selectedDestination,
    destinationOptions,
    quantity,
    maxQuantity,
    errors,
    submitting,
    setDest,
    setQty,
    locationPath,
    submit,
  } = useTransferForm();

  const [pickerOpen, setPickerOpen] = useState(false);

  const { data: thumbs = {} } = usePrimaryEntityImages(
    "product",
    product ? [product.id] : []
  );

  const options = useMemo<PickerOption[]>(
    () =>
      destinationOptions.map((l) => ({
        value: l.id,
        label: l.name,
        sublabel: l.path?.map((p) => p.name).join(" / "),
      })),
    [destinationOptions]
  );

  if (isLoading) {
    return <Hint>Loading…</Hint>;
  }

  return (
    <Container>
      <Scroll>
        <FieldCard
          label="Item"
          icon={
            product ? (
              <EntityThumb url={thumbs[product.id]} alt={product.name} size={32} />
            ) : (
              <Package size={18} />
            )
          }
        >
          <Selected>
            <SelName>{product?.name ?? "Item"}</SelName>
            {product?.sku && <SelSub>SKU: {product.sku}</SelSub>}
          </Selected>
        </FieldCard>

        <FieldCard label="From" icon={<MapPin size={18} />}>
          <Selected>
            <SelName>{sourceLocation?.name ?? "—"}</SelName>
            {sourceLocation && <SelSub>{locationPath(sourceLocation.id)}</SelSub>}
          </Selected>
        </FieldCard>

        <FieldCard
          label="To"
          icon={<MapPin size={18} />}
          onClick={() => setPickerOpen(true)}
          error={errors.destination}
        >
          {selectedDestination ? (
            <Selected>
              <SelName>{selectedDestination.name}</SelName>
              <SelSub>{locationPath(selectedDestination.id)}</SelSub>
            </Selected>
          ) : (
            <Placeholder>Select a destination</Placeholder>
          )}
        </FieldCard>

        <FieldCard
          label="Quantity"
          icon={<Boxes size={18} />}
          trailing={<Units>units</Units>}
          helper={`of ${maxQuantity} available`}
          error={errors.quantity}
        >
          <QtyInput
            type="number"
            inputMode="numeric"
            min={1}
            max={maxQuantity}
            value={quantity}
            onChange={(e) => setQty(Number(e.target.value))}
            aria-label="Quantity to transfer"
          />
        </FieldCard>

        <InfoCallout title="Review before saving">
          This moves the selected quantity to the destination and updates both
          locations&apos; counts.
        </InfoCallout>
      </Scroll>

      <FormFooter
        onCancel={handleClose}
        onSubmit={submit}
        submitting={submitting}
        submitLabel="Transfer Stock"
        submitIcon={<Check size={18} />}
      />

      <PickerSheet
        open={pickerOpen}
        title="Select Destination"
        searchPlaceholder="Search locations…"
        options={options}
        selectedValue={destination}
        onSelect={setDest}
        onClose={() => setPickerOpen(false)}
      />
    </Container>
  );
}

const Container = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
});

const Scroll = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  gap: tokens.space(4),
  padding: tokens.space(4),
  backgroundColor: theme.skuey.surfaceAlt,
  "& > *": { flexShrink: 0 },
}));

const Selected = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 1,
  minWidth: 0,
});

const SelName = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  fontWeight: tokens.font.weight.semibold,
  color: theme.skuey.ink,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const SelSub = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.micro[0],
  color: theme.skuey.muted,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Placeholder = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.subtle,
}));

const Units = styled("span")(({ theme }) => ({
  flexShrink: 0,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.subtle,
}));

const QtyInput = styled("input")(({ theme }) => ({
  width: "100%",
  border: "none",
  outline: "none",
  background: "transparent",
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.body[0],
  fontWeight: tokens.font.weight.semibold,
  color: theme.skuey.ink,
  MozAppearance: "textfield",
  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
}));

const Hint = styled("p")(({ theme }) => ({
  margin: 0,
  padding: tokens.space(6),
  textAlign: "center",
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.muted,
}));
